/**
 * OQ-330 · Khối "Passkey" trong Hồ sơ & bảo mật.
 *
 * Chủ dự án: *"thêm cho tôi tính năng tạo và đăng nhập bằng passkey"*.
 *
 * ── Passkey gắn chặt với tên miền ───────────────────────────────────────
 *
 * Khoá tạo ở `localhost` không dùng được ở `dns.tnvonline.com`, và ngược lại —
 * trình duyệt chỉ đưa khoá cho đúng tên miền đã tạo ra nó.
 *
 * Bản đầu ghi câu ấy thành một dòng chú thích ngay dưới nút; chủ dự án bảo bỏ
 * (OQ-331). Điều đó KHÔNG làm ràng buộc biến mất — nó chỉ không còn được nói ra
 * ở màn hình này. Người dùng gặp nó sẽ thấy trình duyệt báo "không có khoá nào".
 *
 * ── Người dùng bấm Huỷ KHÔNG phải lỗi ──────────────────────────────────
 *
 * Hộp thoại của trình duyệt trả `NotAllowedError` cho cả hai việc rất khác nhau:
 * người dùng bấm thoát, và hết giờ chờ. Dựng một thông báo đỏ cho nó sẽ khiến
 * người ta tưởng máy hỏng trong khi chính họ vừa bấm Huỷ.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  browserSupportsWebAuthn,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useFormat } from '../lib/format';
import { Icon } from '../components/Icon';
import { useToast } from '../components/Toast';

interface PasskeyDto {
  id: string;
  ten: string;
  /** Tên trình quản lý suy từ AAGUID — sự thật khách quan, khác `ten` người dùng đặt. */
  nhaCungCap?: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/** Người dùng bấm Huỷ hoặc hộp thoại hết giờ — không phải lỗi để mà báo. */
function laNguoiDungHuy(err: unknown): boolean {
  return err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError');
}

/** Đoán tên thiết bị để đặt nhãn mặc định dễ nhận ra. */
function doanTenThietBi(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/i.test(ua)) return 'iPhone / iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Passkey';
}

export function PasskeyPanel(): JSX.Element {
  const t = useT();
  const { formatDateTime } = useFormat();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();
  const [dangSua, setDangSua] = useState<string | null>(null);
  const [tenMoi, setTenMoi] = useState('');

  const ds = useQuery({
    queryKey: ['me', 'passkeys'],
    queryFn: () => api.get<{ items: PasskeyDto[] }>('/auth/passkey'),
  });

  const loiGon = (err: unknown): string =>
    err instanceof ApiError ? err.moTaRoRang : t('state.error.title');

  const them = useMutation({
    mutationFn: async () => {
      const { options, luot } = await api.post<{
        options: PublicKeyCredentialCreationOptionsJSON;
        luot: string;
      }>('/auth/passkey/register/begin', {});

      const traLoi = await startRegistration({ optionsJSON: options });
      return api.post<PasskeyDto>('/auth/passkey/register/finish', {
        luot,
        traLoi,
        ten: doanTenThietBi(),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'passkeys'] });
      toast(t('profile.passkey.added'));
    },
    onError: (err) => {
      if (laNguoiDungHuy(err)) return;
      toastError(loiGon(err));
    },
  });

  const doiTen = useMutation({
    mutationFn: (v: { id: string; ten: string }) =>
      api.patch<PasskeyDto>(`/auth/passkey/${v.id}`, { ten: v.ten }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'passkeys'] });
      setDangSua(null);
    },
    onError: (err) => toastError(loiGon(err)),
  });

  const xoa = useMutation({
    mutationFn: (id: string) => api.del<void>(`/auth/passkey/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'passkeys'] });
      toast(t('profile.passkey.removed'));
    },
    onError: (err) => toastError(loiGon(err)),
  });

  const items = ds.data?.items ?? [];

  return (
    <div className="card" data-od-id="passkey-card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('profile.passkey.title')}</h2>
          <div className="sub">{t('profile.passkey.sub')}</div>
        </div>
      </div>

      <div className="card-body stack">
        {!browserSupportsWebAuthn() ? (
          <div className="form-alert" role="note">
            <Icon name="warn" size={16} />
            <span>{t('profile.passkey.unsupported')}</span>
          </div>
        ) : (
          <>
            {items.length === 0 ? (
              <p className="muted">{t('profile.passkey.empty')}</p>
            ) : (
              <div className="table-wrap">
                <table className="tbl" data-od-id="passkey-list">
                  <thead>
                    <tr>
                      <th>{t('profile.passkey.col.name')}</th>
                      <th>{t('profile.passkey.col.created')}</th>
                      <th>{t('profile.passkey.col.lastUsed')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {dangSua === p.id ? (
                            <input
                              type="text"
                              value={tenMoi}
                              maxLength={60}
                              autoFocus
                              onChange={(e) => setTenMoi(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') doiTen.mutate({ id: p.id, ten: tenMoi });
                                if (e.key === 'Escape') setDangSua(null);
                              }}
                            />
                          ) : (
                            <>
                              {p.ten}
                              {/*
                                Hiện nhà cung cấp CHỈ KHI nó khác tên đang đặt.
                                Lặp lại "Bitwarden · Bitwarden" là một dòng thừa
                                mà mắt vẫn phải đọc để nhận ra nó thừa.
                              */}
                              {p.nhaCungCap && p.nhaCungCap !== p.ten ? (
                                <span className="chip mono" style={{ marginLeft: 8 }}>
                                  {p.nhaCungCap}
                                </span>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td className="small muted">{formatDateTime(p.createdAt)}</td>
                        <td className="small muted">
                          {p.lastUsedAt
                            ? formatDateTime(p.lastUsedAt)
                            : t('profile.passkey.neverUsed')}
                        </td>
                        <td>
                          <div className="row-actions">
                            {dangSua === p.id ? (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => doiTen.mutate({ id: p.id, ten: tenMoi })}
                              >
                                {t('common.save')}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => {
                                  setDangSua(p.id);
                                  setTenMoi(p.ten);
                                }}
                              >
                                {t('profile.passkey.rename')}
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost"
                              disabled={xoa.isPending}
                              onClick={() => xoa.mutate(p.id)}
                              data-od-id="passkey-remove"
                            >
                              {t('profile.passkey.remove')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary"
              disabled={them.isPending}
              onClick={() => them.mutate()}
              data-od-id="passkey-add"
            >
              {them.isPending ? t('common.processing') : t('profile.passkey.add')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
